/* iter97 — Daily lists (Canteen + Transport) with PDF exports. */
import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../lib/api';
import { ForkKnife, Bus, Calendar, FileText } from '@phosphor-icons/react';

const todayIso = () => new Date().toISOString().slice(0, 10);

const DailyLists = () => {
  const [tab, setTab] = useState('canteen');
  const [date, setDate] = useState(todayIso());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = tab === 'canteen'
        ? `/lists/canteen/daily?date=${date}`
        : `/lists/transport/daily?date=${date}`;
      const { data } = await api.get(url);
      setData(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur chargement');
    } finally { setLoading(false); }
  }, [tab, date]);

  useEffect(() => { load(); }, [load]);

  const openHistoryPdf = (period) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/lists/${tab}/history?period=${period}&format=pdf`;
    window.open(url, '_blank');
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-4" data-testid="daily-lists-page">
      <div>
        <h1 className="font-outfit text-2xl font-bold flex items-center gap-2">
          <Calendar size={26} className="text-[#0052FF]" weight="duotone" />
          Listes quotidiennes
        </h1>
        <p className="text-sm text-[#64748B] mt-1">
          Cantine, transport — consultation journalière et exports historiques.
        </p>
      </div>

      <div className="flex items-center gap-2 border-b border-[#E2E8F0]">
        <button
          className={`px-4 py-2 text-sm font-semibold flex items-center gap-1.5 ${tab === 'canteen' ? 'text-orange-700 border-b-2 border-orange-600' : 'text-[#64748B]'}`}
          onClick={() => setTab('canteen')} data-testid="tab-canteen">
          <ForkKnife size={16} weight="duotone" /> Cantine
        </button>
        <button
          className={`px-4 py-2 text-sm font-semibold flex items-center gap-1.5 ${tab === 'transport' ? 'text-cyan-700 border-b-2 border-cyan-600' : 'text-[#64748B]'}`}
          onClick={() => setTab('transport')} data-testid="tab-transport">
          <Bus size={16} weight="duotone" /> Transport
        </button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-sm text-[#64748B]">Date :</label>
        <input type="date" value={date}
          onChange={(e) => setDate(e.target.value)}
          className="h-9 px-3 rounded-md border border-[#E2E8F0] text-sm"
          data-testid="daily-date" />
        <div className="flex-1" />
        <span className="text-xs text-[#94A3B8]">Exports historiques :</span>
        <Button size="sm" variant="outline" onClick={() => openHistoryPdf('week')}
          data-testid="export-week"><FileText size={13} className="mr-1" /> Semaine</Button>
        <Button size="sm" variant="outline" onClick={() => openHistoryPdf('month')}
          data-testid="export-month"><FileText size={13} className="mr-1" /> Mois</Button>
        <Button size="sm" variant="outline" onClick={() => openHistoryPdf('quarter')}
          data-testid="export-quarter"><FileText size={13} className="mr-1" /> Trimestre</Button>
      </div>

      <Card className="border border-[#E2E8F0] shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-base">
            {loading ? 'Chargement…' : `${data?.count || 0} enregistrement(s) — ${date}`}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[#64748B] text-[11px] uppercase">
              <tr>
                <th className="text-left py-2 px-3">Élève</th>
                {tab === 'canteen' ? (
                  <>
                    <th className="text-left py-2 px-3">Classe</th>
                    <th className="text-center py-2 px-3">Heure</th>
                  </>
                ) : (
                  <>
                    <th className="text-center py-2 px-3">Ligne</th>
                    <th className="text-center py-2 px-3">Action</th>
                    <th className="text-center py-2 px-3">Heure</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {(data?.items || []).map((it, idx) => (
                <tr key={idx} className="border-t border-[#F1F5F9]">
                  <td className="py-2 px-3 font-semibold">{it.student_name || it.student_id}</td>
                  {tab === 'canteen' ? (
                    <>
                      <td className="py-2 px-3 text-[#475569]">{it.class || '—'}</td>
                      <td className="py-2 px-3 text-center text-[#475569]">
                        {it.scanned_at ? new Date(it.scanned_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="py-2 px-3 text-center text-[#475569]">{it.route_id || '—'}</td>
                      <td className="py-2 px-3 text-center">
                        {it.action === 'boarded' ? '↑ Montée' : it.action === 'alighted' ? '↓ Descente' : it.action}
                      </td>
                      <td className="py-2 px-3 text-center text-[#475569]">
                        {it.scanned_at ? new Date(it.scanned_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}
                      </td>
                    </>
                  )}
                </tr>
              ))}
              {!(data?.items || []).length && !loading && (
                <tr><td colSpan={tab === 'canteen' ? 3 : 4} className="text-center py-8 text-[#94A3B8]">
                  Aucun enregistrement pour cette date.
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
};

export default DailyLists;
